import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import LoadingScreen from "./LoadingScreen";
import { motion } from "framer-motion";
import UsageDashboard from "./UsageDashboard";
import apiService from "../services/apiService";
import { useOrganization } from "../context/OrganizationContext";
import { useProject } from "../context/ProjectContext";

const Analytics = () => {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const { selectedProject, loading: projectLoading } = useProject();
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [projectApiKeys, setProjectApiKeys] = useState([]);
  const [loadingApiKeys, setLoadingApiKeys] = useState(false);
  const [isApiKeysListExpanded, setIsApiKeysListExpanded] = useState(true);

  // Reset selected API key when project changes
  useEffect(() => {
    setSelectedApiKey(null);
  }, [selectedProject]);

  // Load API keys for the selected project
  useEffect(() => {
    const loadApiKeys = async () => {
      if (!selectedProject?.id || !currentOrganization?.id) return;

      setLoadingApiKeys(true);
      try {
        const keys = await apiService.getApiKeys(
          currentOrganization.id,
          selectedProject.uuid,
        );
        setProjectApiKeys(keys);

        // Single key: default to that key (detail). Multiple: combined "All keys" (null).
        if (keys.length === 1) {
          setSelectedApiKey(keys[0]);
        } else {
          setSelectedApiKey(null);
        }
      } catch (error) {
        console.error("Error loading API keys:", error);
        toast.error("Failed to load API keys");
      } finally {
        setLoadingApiKeys(false);
      }
    };

    loadApiKeys();
  }, [selectedProject, currentOrganization]);

  // Verify authentication
  useEffect(() => {
    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");

    if (!token || !email) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  if (projectLoading) {
    return <LoadingScreen />;
  }

  return (
    <div>
      <div className="min-h-screen bg-[#131313]">
        <header className="sticky top-0 z-10 border-b border-white/[0.06] bg-[#131313]">
          <div className="px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="font-title text-2xl font-bold tracking-tight text-white">
                  Analytics & usage
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="mx-auto max-w-screen-2xl py-2 ">
          <p className="font-secondary my-4 text-md text-white/65">
            Monitor your API usage and performance metrics
          </p>
          {!selectedProject ? (
            <div className="rounded-sm border border-white/10 bg-landing-section px-6 py-12 text-center sm:px-10">
              <svg
                className="mx-auto mb-4 h-16 w-16 text-primary/40"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                />
              </svg>
              <h3 className="font-title text-xl font-semibold tracking-tight text-white">
                No project selected
              </h3>
              <p className="font-secondary mt-2 text-sm text-white/65">
                Please select a project from the dropdown in the sidebar to view
                analytics.
              </p>
            </div>
          ) : loadingApiKeys ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : projectApiKeys.length === 0 ? (
            <div className="rounded-sm border border-white/10 bg-landing-section px-6 py-12 text-center sm:px-10">
              <svg
                className="mx-auto mb-4 h-16 w-16 text-primary/40"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              <h3 className="font-title text-xl font-semibold tracking-tight text-white">
                No API keys
              </h3>
              <p className="font-secondary mt-2 text-sm text-white/65">
                Create an API key in the Dashboard to start viewing analytics.
              </p>
              <motion.button
                type="button"
                onClick={() => navigate("/dashboard")}
                className="font-secondary mt-8 inline-flex items-center gap-2 rounded-sm border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Go to Dashboard
              </motion.button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* API key picker — collapsible (same pattern as Dashboard API keys) */}
              <div className="overflow-hidden rounded-sm border border-white/[0.06] bg-landing-section">
                <button
                  type="button"
                  onClick={() => setIsApiKeysListExpanded((v) => !v)}
                  className="font-secondary flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.03] sm:px-5 sm:py-4"
                  aria-expanded={isApiKeysListExpanded}
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-title text-base font-semibold tracking-tight text-white sm:text-lg">
                      API keys
                    </span>
                    <span className="font-secondary ml-2 text-sm text-white/50">
                      ({projectApiKeys.length})
                    </span>
                  </div>
                  <svg
                    className={`h-5 w-5 shrink-0 text-white/45 transition-transform duration-200 ${
                      isApiKeysListExpanded ? "rotate-180" : ""
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
                {isApiKeysListExpanded && (
                  <div className="border-t border-white/10 p-4 sm:p-5">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <p className="font-secondary text-sm text-white/65">
                        {projectApiKeys.length === 1 && selectedApiKey
                          ? `Analytics for your API key: ${selectedApiKey.name}`
                          : selectedApiKey
                            ? `Detail view: ${selectedApiKey.name}`
                            : `Default: combined analytics for all ${projectApiKeys.length} key${projectApiKeys.length !== 1 ? "s" : ""} in one card.`}
                      </p>
                      {selectedApiKey && projectApiKeys.length > 1 && (
                        <motion.button
                          type="button"
                          onClick={() => setSelectedApiKey(null)}
                          className="font-secondary inline-flex shrink-0 items-center gap-2 self-start rounded-sm border border-white/15 bg-transparent px-4 py-2 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                          Back to all keys
                        </motion.button>
                      )}
                    </div>
                    <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4 md:gap-4">
                      {projectApiKeys.length > 1 && (
                        <motion.button
                          type="button"
                          onClick={() => setSelectedApiKey(null)}
                          className={`font-secondary group flex min-h-[148px] min-w-0 flex-col rounded-sm border p-4 text-left transition-all ${
                            selectedApiKey == null
                              ? "border-primary bg-primary/10 ring-1 ring-primary/35"
                              : "border-white/[0.08] bg-[#0c0c0c] hover:border-white/20 hover:bg-white/[0.02]"
                          }`}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                        >
                          <div className="mb-3 flex items-start justify-between gap-2">
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border ${
                                selectedApiKey == null
                                  ? "border-primary/40 bg-primary/15 text-primary"
                                  : "border-white/10 bg-white/[0.04] text-primary/90"
                              }`}
                            >
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                                />
                              </svg>
                            </div>
                            {selectedApiKey == null && (
                              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary">
                                <svg
                                  className="h-3.5 w-3.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                  aria-hidden
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </span>
                            )}
                          </div>
                          <h4 className="font-title line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-snug text-white sm:text-base">
                            All keys
                          </h4>
                          <p className="mt-1 text-[11px] leading-relaxed text-white/50 sm:text-xs">
                            Merged totals & charts
                          </p>
                          <div className="mt-auto flex items-center justify-between gap-2 pt-3">
                            <span className="inline-flex items-center rounded-sm border border-primary/25 bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary sm:text-xs">
                              All
                            </span>
                            <span
                              className={`text-[10px] font-semibold sm:text-xs ${
                                selectedApiKey == null
                                  ? "text-primary"
                                  : "text-white/35 group-hover:text-white/50"
                              }`}
                            >
                              {selectedApiKey == null ? "Selected" : "Select"}
                            </span>
                          </div>
                        </motion.button>
                      )}
                      {projectApiKeys.map((apiKey) => {
                        const isSelected = selectedApiKey?.id === apiKey.id;
                        return (
                          <motion.button
                            type="button"
                            key={apiKey.id}
                            onClick={() => setSelectedApiKey(apiKey)}
                            className={`font-secondary group flex min-h-[148px] min-w-0 flex-col rounded-sm border p-4 text-left transition-all ${
                              isSelected
                                ? "border-primary bg-primary/10 ring-1 ring-primary/35"
                                : "border-white/[0.08] bg-[#0c0c0c] hover:border-white/20 hover:bg-white/[0.02]"
                            }`}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                          >
                            <div className="mb-3 flex items-start justify-between gap-2">
                              <div
                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border ${
                                  isSelected
                                    ? "border-primary/40 bg-primary/15 text-primary"
                                    : "border-white/10 bg-white/[0.04] text-primary/90"
                                }`}
                              >
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                                  />
                                </svg>
                              </div>
                              {isSelected && (
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary">
                                  <svg
                                    className="h-3.5 w-3.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    aria-hidden
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </span>
                              )}
                            </div>
                            <h4 className="font-title line-clamp-2 min-h-[2.5rem] text-sm font-semibold leading-snug text-white sm:text-base">
                              {apiKey.name}
                            </h4>
                            <p className="mt-1 line-clamp-2 break-all font-mono text-[11px] leading-relaxed text-primary/85 sm:text-xs">
                              {apiKey.key.substring(0, 24)}…
                            </p>
                            <div className="mt-auto flex items-center justify-between gap-2 pt-3">
                              <span
                                className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide sm:text-xs ${
                                  apiKey.is_active
                                    ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300"
                                    : "border-red-500/25 bg-red-500/10 text-red-300"
                                }`}
                              >
                                {apiKey.is_active ? "Active" : "Inactive"}
                              </span>
                              <span
                                className={`text-[10px] font-semibold sm:text-xs ${
                                  isSelected
                                    ? "text-primary"
                                    : "text-white/35 group-hover:text-white/50"
                                }`}
                              >
                                {isSelected ? "Selected" : "Select"}
                              </span>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Single card: merged "all keys" or one key detail */}
              {projectApiKeys.length > 0 && (
                <div className="rounded-sm border border-white/10 bg-landing-section p-4 sm:p-6">
                  {selectedApiKey ? (
                    <UsageDashboard apiKey={selectedApiKey} />
                  ) : (
                    <UsageDashboard apiKeys={projectApiKeys} />
                  )}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Analytics;
